import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { CloseOutlined, DesktopOutlined, FolderOpenOutlined } from '@ant-design/icons';
import type { DicomRow } from 'services/exploreService';
import { ViewChips, fmt, studyHref, viewerHref } from '.';

interface Props {
  items: DicomRow[];
  index: number | null;
  onIndex: (i: number | null) => void;
  /** Offset of items[0] within the full result set, for the "n of total" label. */
  offset?: number;
  total?: number;
}

const yesNo = (v: boolean) => (v ? <span style={{ color: 'var(--good)' }}>yes</span> : <span className="ax-faint">no</span>);

/**
 * Full-screen look at one DICOM from a grid: the cine loop (or still) on the left, the
 * metadata on the right. ←/→ step through the grid, Esc closes, V opens the full viewer.
 */
const DicomInspector: React.FC<Props> = ({ items, index, onIndex, offset = 0, total }) => {
  const d = index != null ? items[index] : undefined;
  const [mediaStep, setMediaStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => setMediaStep(0), [d?.uuid]);

  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === 'escape') onIndex(null);
      else if (k === 'arrowright' && index < items.length - 1) onIndex(index + 1);
      else if (k === 'arrowleft' && index > 0) onIndex(index - 1);
      else if (k === 'v' && d)
        navigate(viewerHref(d.icid), { state: d.study_icid ? { study: d.study_icid } : undefined });
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onIndex, d, navigate]);

  // Preload the next poster so stepping feels instant.
  useEffect(() => {
    const next = index != null ? items[index + 1] : undefined;
    if (next?.media.poster) new Image().src = next.media.poster;
  }, [index, items]);

  if (!d) return null;

  const multi = (d.number_of_frames ?? 0) > 1;
  // Try the cine loop first for multi-frame clips, then the still render, then the first frame.
  const sources = [
    multi && d.media.cine ? { kind: 'video', src: d.media.cine } : null,
    d.media.still ? { kind: 'img', src: d.media.still } : null,
    d.media.poster ? { kind: 'img', src: d.media.poster } : null,
  ].filter(Boolean) as { kind: 'video' | 'img'; src: string }[];
  const source = sources[mediaStep];

  const rows: [string, React.ReactNode][] = [
    ['Study', d.study_icid ? <Link to={studyHref(d.study_icid)}>{d.study_icid}</Link> : '–'],
    ['Index in study', d.index ?? '–'],
    ['Scanner', [d.manufacturer, d.model].filter(Boolean).join(' · ') || '–'],
    ['Type', d.type ?? '–'],
    ['Frames', fmt(d.number_of_frames)],
    ['Frame rate', d.frames_per_second ? `${d.frames_per_second.toFixed(1)} fps` : '–'],
    ['Size', d.pixel_width && d.pixel_height ? `${d.pixel_width} × ${d.pixel_height}` : '–'],
    ['AI view', d.predicted_view ? `${d.predicted_view}${d.predicted_confidence != null ? ` (${(100 * d.predicted_confidence).toFixed(0)}%)` : ''}` : '–'],
    ['Tech view', d.labeled_view ?? '–'],
    ['Segmentation', yesNo(d.has_segmentation)],
    ['Keypoints', yesNo(d.has_keypoints)],
    ['Quality label', yesNo(d.has_quality_label)],
    ['Annotation', d.skipped_annotations ? <span style={{ color: 'var(--warn)' }}>skipped</span> : '–'],
  ];

  return (
    <div className="ax-inspector" role="dialog" aria-modal="true" aria-label={`DICOM ${d.icid}`}>
      <div className="stage" onClick={e => e.target === e.currentTarget && onIndex(null)}>
        {source ? (
          source.kind === 'video' ? (
            <video key={source.src} src={source.src} autoPlay loop muted playsInline controls onError={() => setMediaStep(s => s + 1)} />
          ) : (
            <img key={source.src} src={source.src} alt={`DICOM ${d.icid}`} referrerPolicy="no-referrer" onError={() => setMediaStep(s => s + 1)} />
          )
        ) : (
          <div className="ax-empty">
            <b>No rendered media</b>Open the viewer to download the original DICOM.
          </div>
        )}
        <button type="button" className="nav l" aria-label="Previous DICOM" disabled={index === 0} onClick={() => onIndex(index! - 1)}>
          ‹
        </button>
        <button type="button" className="nav r" aria-label="Next DICOM" disabled={index === items.length - 1} onClick={() => onIndex(index! + 1)}>
          ›
        </button>
      </div>
      <div className="side">
        <div className="head">
          <div>
            <h2>{d.icid}</h2>
            <span className="pos">
              {fmt(offset + index! + 1)} of {fmt(total ?? items.length)}
            </span>
          </div>
          <button type="button" className="ax-icon-btn" aria-label="Close" onClick={() => onIndex(null)}>
            <CloseOutlined />
          </button>
        </div>
        <ViewChips predicted={d.predicted_view} labeled={d.labeled_view} confidence={d.predicted_confidence} />
        <dl className="ax-kv">
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </React.Fragment>
          ))}
        </dl>
        <div className="acts">
          <Link to={viewerHref(d.icid)} state={d.study_icid ? { study: d.study_icid } : undefined}>
            <Button type="primary" icon={<DesktopOutlined />} block>
              Open viewer
            </Button>
          </Link>
          {d.study_icid && (
            <Link to={studyHref(d.study_icid)}>
              <Button icon={<FolderOpenOutlined />} block>
                Open study
              </Button>
            </Link>
          )}
        </div>
        <div className="keys">
          <kbd>←</kbd>/<kbd>→</kbd> previous/next · <kbd>V</kbd> open in viewer · <kbd>Esc</kbd> close
          <br />
          The viewer shows labels, segmentations, keypoints and model predictions for this clip.
        </div>
      </div>
    </div>
  );
};

export default DicomInspector;
